import { Suspense, useCallback, useState } from 'react'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import {
  Event,
  NetworkVisibility,
  RecurringEventUpdateMode,
} from '@tribeplatform/gql-client/types'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { CommandBarHotKey } from '../../CommandBar/components/CommandBarHotKey.js'
import {
  useActiveContentModule,
  useContentModuleUtils,
  useContentViewMode,
} from '../../common/index.js'
import { getDomainInfo } from '../../common/utils/domain.js'
import { T, useI18n } from '../../i18n/index.js'
import { Link } from '../../Link/Link.js'
import { useWebShare } from '../../utils/react-use-web-share.js'
import { useMobileDetect } from '../../utils/use-mobile-detect-hook.js'
import { isRecurringEvent } from '../EventDetails/utils.js'
import { useEventActions } from '../hooks/useEventActions.js'

import { ShareModal } from './ShareModal/ShareModal.js'

interface Props {
  event: Event
}

export const EventHeaderActions = ({ event }: Props) => {
  const { isModalView } = useContentViewMode()
  const { clearActiveContentModule } = useActiveContentModule()
  const [canUpdateEvent, canDeleteEvent] = hasScopesPermission(
    event.authMemberProps?.permissions,
    ['updateEvent', 'deleteEvent'],
  )
  const { data: network } = useNetwork()
  const [showShareModal, setShowShareModal] = useState(false)
  const { isSupported, share } = useWebShare()
  const detectMobile = useMobileDetect()
  const { getResourceUrl } = getDomainInfo({
    domain: network?.domain,
    domainSubfolder: network?.domainSubfolder,
  })
  const { handleDeleteEvent } = useEventActions()
  const { linkToEditContentForm } = useContentModuleUtils()

  const isPrivate =
    event?.space?.private || network?.visibility === NetworkVisibility.private
  const shareUrl = getResourceUrl(event.relativeUrl)

  const handleShare = useCallback(() => {
    if (!isPrivate && isSupported && detectMobile.isMobile()) {
      share({ title: event.title, url: shareUrl })
    } else {
      setShowShareModal(true)
    }
  }, [isPrivate, isSupported, detectMobile, share, event.title, shareUrl])
  const isRecurring = isRecurringEvent(event)

  return (
    <div className="flex items-center justify-end border-b border-line-subdued py-2 px-4 sm:px-6">
      <Dropdown>
        <Dropdown.ButtonMinimal as="div">
          <IconButton
            icon={<SvgIcon name="dots-horizontal" />}
            variant="tertiaryNeutral"
          />
        </Dropdown.ButtonMinimal>
        <Dropdown.Items>
          {canUpdateEvent && (
            <Dropdown.Item
              leadingIcon={<SvgIcon name="edit-2" />}
              as={Link}
              href={linkToEditContentForm({
                relativeUrl: event.relativeUrl,
                returnTo: isModalView ? event.relativeUrl : undefined,
              })}
              hotkey="e"
            >
              <T id="Event.HeaderActions.Edit" defaultMessage="Edit event" />
            </Dropdown.Item>
          )}
          {isModalView && (
            <Dropdown.Item
              leadingIcon={<SvgIcon name="eye" />}
              as={Link}
              href={event.relativeUrl}
              target="_blank"
              hotkey="v"
            >
              <T id="Event.HeaderActions.View" defaultMessage="View event" />
            </Dropdown.Item>
          )}
          <Dropdown.Item
            leadingIcon={<SvgIcon name="share" />}
            onClick={handleShare}
          >
            <T id="Event.HeaderActions.Share" defaultMessage="Share event" />
          </Dropdown.Item>
          {canDeleteEvent && !isRecurring && (
            <Dropdown.Item
              leadingIcon={<SvgIcon name="trash" />}
              onClick={() =>
                handleDeleteEvent(event, RecurringEventUpdateMode.thisEventOnly)
              }
            >
              <T
                id="Event.HeaderActions.Delete"
                defaultMessage="Delete event"
              />
            </Dropdown.Item>
          )}
          {canDeleteEvent && isRecurring && (
            <RecurringEventDeleteDropdown event={event} />
          )}
        </Dropdown.Items>
      </Dropdown>
      {isModalView && (
        <IconButton
          icon={<SvgIcon name="close" />}
          variant="tertiaryNeutral"
          onClick={clearActiveContentModule}
        />
      )}
      {showShareModal && (
        <Suspense>
          <ShareModal
            open
            isPrivate={isPrivate}
            onClose={() => setShowShareModal(false)}
            title={event.title}
            eventId={event.id}
            url={shareUrl}
          />
        </Suspense>
      )}
      <EventActionHotkeys event={event} />
    </div>
  )
}

const EventActionHotkeys = ({ event }: { event: Event }) => {
  const { $t } = useI18n()
  const { isModalView } = useContentViewMode()
  const [canUpdateEvent] = hasScopesPermission(
    event.authMemberProps?.permissions,
    ['updateEvent'],
  )
  const { linkToEditContentForm } = useContentModuleUtils()

  return (
    <>
      {canUpdateEvent && (
        <CommandBarHotKey
          actionItem={{
            name: $t({
              id: 'Event.HeaderActions.Edit',
              defaultMessage: 'Edit event',
            }),
            hotkey: 'e',
            url: linkToEditContentForm({
              relativeUrl: event.relativeUrl,
              returnTo: !isModalView ? event.relativeUrl : undefined,
            }),
          }}
        />
      )}
      {isModalView && (
        <CommandBarHotKey
          actionItem={{
            name: $t({
              id: 'Event.HeaderActions.View',
              defaultMessage: 'View event',
            }),
            hotkey: 'v',
            url: event.relativeUrl,
          }}
        />
      )}
    </>
  )
}

interface RecurringEventDeleteDropdownProps {
  event: Event
}

const RecurringEventDeleteDropdown = ({
  event,
}: RecurringEventDeleteDropdownProps) => {
  const { handleDeleteEvent } = useEventActions()

  return (
    <Dropdown className="w-full" placement="bottom-end">
      <Dropdown.ButtonMinimal as="div">
        <Dropdown.Item leadingIcon={<SvgIcon name="trash" />}>
          <T id="Event.HeaderActions.Delete" defaultMessage="Delete event" />
        </Dropdown.Item>
      </Dropdown.ButtonMinimal>
      <Dropdown.Items>
        <div className="flex flex-col gap-2 p-2">
          <div className="flex items-center gap-2">
            <SvgIcon name="trash" className="text-content-subdued" size="lg" />
            <Text className="font-medium">
              <T
                id="Event.HeaderActions.Delete"
                defaultMessage="Delete event"
              />
            </Text>
          </div>
          <Text className="text-content-muted">
            <T
              id="Event.RecurringEvent.Delete.Description"
              defaultMessage="Which events would you like to delete?"
            />
          </Text>
          <Divider padding="sm" />
        </div>
        <Dropdown.Item
          leadingIcon={<SvgIcon name="calendar-minus" />}
          onClick={() =>
            handleDeleteEvent(event, RecurringEventUpdateMode.thisEventOnly)
          }
        >
          <Text className="font-medium">
            <T
              id="Event.RecurringEvent.Actions.ThisEvent"
              defaultMessage="Only this event"
            />
          </Text>
        </Dropdown.Item>
        <Dropdown.Item
          leadingIcon={<SvgIcon name="calendar-x-1" />}
          onClick={() =>
            handleDeleteEvent(
              event,
              RecurringEventUpdateMode.thisAndFutureEvents,
            )
          }
        >
          <Text className="font-medium">
            <T
              id="Event.RecurringEvent.Actions.ThisAndFollowingEvents"
              defaultMessage="This and following events"
            />
          </Text>
        </Dropdown.Item>
        <Dropdown.Item
          leadingIcon={<SvgIcon name="calendar-slash" />}
          onClick={() =>
            handleDeleteEvent(event, RecurringEventUpdateMode.allEvents)
          }
        >
          <Text className="font-medium">
            <T
              id="Event.RecurringEvent.Actions.AllEvents"
              defaultMessage="All events in the series"
            />
          </Text>
        </Dropdown.Item>
      </Dropdown.Items>
    </Dropdown>
  )
}
